import React, { useState, useEffect, useRef } from 'react'
import { selectChallenge, selectExperienceLevel, selectSkillName } from '../../../redux/skillSlice'
import { useSelector, useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { Check } from 'lucide-react'
import { saveUserInfo, selectUserInfo, fetchPersonalizedSyllabus, fetchPersonalizedSyllabusForBeginner } from '../../../redux/userInfoSlice'
import { selectInitSessionUserDetails } from '../../../redux/onboardingUserDetails'
import { fetchSubscriptionStatusWithPolling } from '../../../redux/subscriptionStatusSlice'
import { getUserRole } from '../../../redux/authenticationSlice'
import { Mixpanel } from '../../../config/Mixpanel'
import constants from '../../../utilities/constants'

// Local capitalise function as fallback
const capitalise = (str = "") => {
    if (str === '' || !str) return '';
    return str?.[0]?.toUpperCase() + str?.slice(1);
}

const LoaderFour = () => {
    const [scanPosition, setScanPosition] = useState(0)
    const [scanDirection, setScanDirection] = useState(1) // 1 for down, -1 for up
    const [searchText, setSearchText] = useState('Preparing your course...')
    const [progressPercentage, setProgressPercentage] = useState(10)
    const [loaderStatus, setLoaderStatus] = useState('loading')
    const [retryCount, setRetryCount] = useState(0)
    const hasApiCallStarted = useRef(false)

  const dispatch = useDispatch()
  const navigate = useNavigate()
  
  const skillName = useSelector(selectSkillName)
  const userInfo = useSelector(selectUserInfo)
  const userSessionDetails = useSelector(selectInitSessionUserDetails)

  const experienceLevel = useSelector(selectExperienceLevel)
    const challenge = useSelector(selectChallenge)
    const challengeLabel = challenge
      ? capitalise(challenge.split('_').join(' '))
      : 'Goal detected'


    
    // Scanning line animation - up and down continuously
    useEffect(() => {
        // Track loader page load
        Mixpanel.track(constants.Mixpanel.ONBOARDING_V2_LOADER_FOUR_LOADED, {
            skill: skillName,
            experienceLevel: experienceLevel,
            challenge: challenge,
            session_id: userSessionDetails?.session_id
        })

        const interval = setInterval(() => {
            setScanPosition(prev => {
                const newPosition = prev + (scanDirection * 1.5)

                // If reached bottom, change direction to go up
                if (newPosition >= 95) {
                    setScanDirection(-1)
                    return 95
                }

                // If reached top, change direction to go down
                if (newPosition <= 5) {
                    setScanDirection(1)
                    return 5
                }

                return newPosition
            })
        }, 30)

        return () => clearInterval(interval)
    }, [scanDirection, skillName, experienceLevel, challenge, userSessionDetails?.session_id])

  // Fetch the personalized syllabus and drive loader progress from real API stages.
  useEffect(() => {
      if (!userSessionDetails?.session_id || hasApiCallStarted.current) return

      hasApiCallStarted.current = true

      const fetchSyllabus = async () => {
        try {
            setSearchText('Checking your course access...')
            setProgressPercentage(20)

            await dispatch(fetchSubscriptionStatusWithPolling({ 
                email: userInfo?.email,
                pollingInterval: 2000, // Poll every 2 seconds
                maxAttempts: 30 // Maximum 30 attempts (60 seconds total)
            })).unwrap()

            setSearchText('Preparing your course profile...')
            setProgressPercentage(45)
            await dispatch(getUserRole({ email: userInfo?.email })).unwrap()

            setSearchText(`Building your personalised ${capitalise(skillName)} syllabus...`)
            setProgressPercentage(70)
            await dispatch(fetchPersonalizedSyllabus({
              session_id: userSessionDetails?.session_id
            })).unwrap()

            setSearchText('Course personalisation complete!')
            setProgressPercentage(100)
            setLoaderStatus('success')
        } catch (error) {
          console.error('Error fetching personalized syllabus:', error);
          setSearchText('We could not build your course. Please try again.')
          setLoaderStatus('error')
        }
      };

      const fetchSyllabusBeginner = async () => {
        try {

            if(userInfo.name && userInfo.email && userInfo.phone) {
                setSearchText('Saving your details...')
                setProgressPercentage(25)
                await dispatch(saveUserInfo({
                  session_id: userSessionDetails.session_id,
                  name: userInfo.name,
                    email: userInfo.email,
                    phone: userInfo.phone
                  })).unwrap()
                }

            dispatch(fetchSubscriptionStatusWithPolling({ 
                email: userInfo?.email,
                pollingInterval: 2000, // Poll every 2 seconds
                maxAttempts: 30 // Maximum 30 attempts (60 seconds total)
            }))
            dispatch(getUserRole({ email: userInfo?.email }))

            setSearchText(`Building your personalised ${capitalise(skillName)} syllabus...`)
            setProgressPercentage(70)
            await dispatch(fetchPersonalizedSyllabusForBeginner({
              skill_name: skillName,
              goal: challenge?.toLowerCase()?.split(' ').join('_')
            })).unwrap()

            setSearchText('Course personalisation complete!')
            setProgressPercentage(100)
            setLoaderStatus('success')
        } catch (error) {
          console.error('Error fetching personalized syllabus:', error);
          setSearchText('We could not build your course. Please try again.')
          setLoaderStatus('error')
        }
      };

      if(experienceLevel === 'beginner') {
        fetchSyllabusBeginner();
      } else {
        fetchSyllabus();
      }
    
  }, [challenge, dispatch, experienceLevel, retryCount, skillName, userInfo.email, userInfo.name, userInfo.phone, userSessionDetails?.session_id])

    const [gifLoaded, setGifLoaded] = useState(true)
    // Eager load the celebration GIF
    useEffect(() => {
        const img = new Image()
        img.src = "https://assets.learntube.ai/files/Post%20Payment/Payment%20done.gif?updatedAt=1705071773800"
        // Preload the image so it's ready when seconds >= 12
    }, [])

    useEffect(() => {
        if(loaderStatus === 'success') {
            const imageTimer = setTimeout(() => {
                setGifLoaded(false)
            }, 400)
            const navigationTimer = setTimeout(() => {
                navigate('/onboarding/pricing', { replace: true })
            }, 800)

            return () => {
                clearTimeout(imageTimer)
                clearTimeout(navigationTimer)
            }
        }
    }, [loaderStatus, navigate])

    const handleRetry = () => {
        hasApiCallStarted.current = false
        setLoaderStatus('loading')
        setProgressPercentage(10)
        setSearchText('Preparing your course...')
        setRetryCount(prev => prev + 1)
    }

    // API call to save user info and then fetch analysis when component mounts

    return (

        <div className='flex flex-col h-full gap-6 justify-between items-center'>
            <style>{`
        @keyframes lt-pop {
          0% { opacity: 0; transform: scale(0.6) translateY(6px); }
          60% { opacity: 1; transform: scale(1.08) translateY(0px); }
          100% { opacity: 1; transform: scale(1) translateY(0px); }
        }
        .lt-pop {
          transform-origin: center;
          animation: lt-pop 520ms cubic-bezier(0.2, 0.85, 0.2, 1) both;
          will-change: transform, opacity;
        }
        .line-clamp-3 {
          display: -webkit-box;
          -webkit-line-clamp: 3;
          -webkit-box-orient: vertical;
          overflow: hidden;
          text-overflow: ellipsis;
        }
      `}</style>
            <div className="w-full md:max-w-[45rem] flex-1 justify-center items-center mx-auto px-4 pb-4 lg:pb-6 lg:px-6 text-white relative flex flex-col">

                {/* Main Content Container */}
                <div className="max-w-2xl mx-auto text-center relative z-10">

                    {/* Circular Loader with Skills */}
                    {loaderStatus !== 'success' ? <div className="mb-12 relative flex items-center justify-center">
                        <div className="relative">

                            {/* Outer Circle - Dark Navy */}
                            <div className="w-[200px] h-[200px] rounded-full bg-[#072649]  flex items-center justify-center">

                                {/* Inner Circle - Blue */}
                                <div className="w-40 h-40 rounded-full bg-[#406AFF]/30 border border-[#406AFF] shadow-inner relative flex items-center justify-center">

                                    {/* Central Square with Scanning Effect */}
                                    <div className="w-20 h-20 bg-[#07162F] rounded-[8px] relative overflow-hidden flex items-center justify-center shadow-lg">
                                        {/* Refresh Icon */}
                                        <img src="https://assets.learntube.ai/files/personalise/ri_search-ai-2-line%20(1).webp" className='w-[24px] h-[24px]' alt="" />

                                        {/* Scanning Line */}
                                        <div
                                            className="absolute w-full h-0.5 z-10"
                                            style={{
                                                top: `${scanPosition}%`,
                                                backgroundImage:
                                                    'linear-gradient(90deg, rgba(64, 106, 255, 0) 0%, rgba(64, 106, 255, 1) 57%, rgba(64, 106, 255, 0) 100%)',
                                                opacity: 1,
                                                filter: 'blur(0.5px)',
                                                boxShadow: '0 0 10px rgba(64, 106, 255, 0.9), 0 0 20px rgba(64, 106, 255, 0.6)'
                                            }}
                                        ></div>

                                        {/* Additional glow effect */}
                                        <div
                                            className="absolute w-full h-1 z-5"
                                            style={{
                                                top: `${scanPosition}%`,
                                                backgroundImage:
                                                    'linear-gradient(90deg, rgba(64, 106, 255, 0) 0%, rgba(64, 106, 255, 0.3) 57%, rgba(64, 106, 255, 0) 100%)',
                                                filter: 'blur(2px)'
                                            }}
                                        ></div>
                                    </div>
                                </div>
                            </div>

                            {/* Floating Skill Labels */}
                            <div className="absolute inset-0 w-[200px] h-[200px]">
                                {/* Strategy - Top Right */}
                                <div className="absolute" style={{
                                    top: '15%',
                                    right: '-15%',
                                }}>
                                    <div
                                        className="lt-pop bg-[#406AFF] text-white px-4 py-2 capitalize rounded-full text-[8px] font-medium shadow-lg hover:scale-105 transition-transform w-[90px] flex items-center justify-center"
                                        style={{ animationDelay: '1s' }}
                                    >
                                        <p className='text-center line-clamp-3 overflow-hidden leading-tight'>
                                          {experienceLevel}
                                        </p>
                                    </div>
                                </div>

                                {/* Product Thinking - Left */}
                                <div className="absolute" style={{
                                    top: '45%',
                                    left: '-20%',
                                }}>
                                    <div
                                        className="lt-pop bg-[#406AFF] text-white px-4 py-2 rounded-full text-[8px] font-medium shadow-lg hover:scale-105 transition-transform w-[90px] flex items-center justify-center"
                                        style={{ animationDelay: '3s' }}
                                    >
                                        <p className='text-center line-clamp-3 overflow-hidden leading-tight'>
                                          {challengeLabel}
                                        </p>
                                    </div>
                                </div>

                                {/* User Research - Bottom Right */}
                                <div className="absolute" style={{
                                    bottom: '2%',
                                    right: '-5%',
                                }}>
                                    <div
                                        className="lt-pop bg-[#406AFF] text-white px-4 py-2 rounded-full text-[8px] font-medium shadow-lg hover:scale-105 transition-transform w-[90px] flex items-center justify-center"
                                        style={{ animationDelay: '7s' }}
                                    >
                                        <p className='text-center line-clamp-3 overflow-hidden leading-tight'>
                                          Saving 6+ Hours
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div> : 
                    (gifLoaded ? (
                        <img
                          src="https://assets.learntube.ai/files/Post%20Payment/Payment%20done.gif?updatedAt=1705071773800"
                         className='mx-auto my-10 size-[170px]'
                         alt="Payment done"
                        />
                      ) : (
                        <img
                          src="https://assets.learntube.ai/files/dataschool/Payment%20done%201.png?updatedAt=1708062850500"
                         className='mx-auto my-10 size-[170px]'
                         alt="Payment done"
                        />
                      ))
                    
                    }

                    {/* Main Title with Gradient Animation */}
                    <div className="mt-8">
                        <div className='flex flex-col'>
                            <h1 className='text-center bg-gradient-to-r from-white to-[#FFE994] bg-clip-text text-transparent font-bold text-[1.75rem] leading-[130%] capitalize'>
                               {loaderStatus !== 'success' ? `Building your Perfect ${capitalise(skillName)} Course...` : `your Perfect ${capitalise(skillName)} Course is ready!`}
                            </h1>
                        </div>
                    </div>

                    {/* Bottom Status with Progress Bar */}
                    <div className='flex flex-col gap-2'>
                        <div className="mt-6 flex flex-col gap-3">
                            {/* Status Text */}
                            <div className="flex flex-col gap-5  py-4 px-4 rounded-[12px]" style={{
                                backgroundImage: `linear-gradient(
                to bottom,
                rgba(64, 106, 255, 0.5) 0%,
                rgba(64, 106, 255, 0) 100%
              )`
                            }}>
                                {loaderStatus !== 'success' ? <div className='flex items-center justify-center gap-2'>
                                    <div className="w-[14px] h-[14px] bg-[#54B226] p-[2px] rounded-full flex items-center justify-center animate-pulse">
                                        <Check className='w-full h-full text-white' />
                                    </div>
                                    <p className="text-[14px] text-white/50 font-normal ml-1 text-left capitalize">
                                        {searchText}
                                    </p>
                                </div> : 
                                 <p className="text-[14px] text-white/50 font-normal ml-1 text-center capitalize">
                                 Redirecting to your course...
                             </p>
                                }

                                {loaderStatus === 'error' && (
                                    <button
                                        type="button"
                                        onClick={handleRetry}
                                        className="mx-auto rounded-lg bg-[#7FC241] px-5 py-2 text-sm font-semibold text-[#121212] hover:bg-[#8bd845]"
                                    >
                                        Try again
                                    </button>
                                )}



                                <div className="">
                            {/* Progress Bar */}
                            <div className="w-full bg-[#072649] rounded-full h-2 mb-3 overflow-hidden">
                                <div
                                    className="h-full bg-gradient-to-r from-[#406AFF] to-[#64B5FF] rounded-full transition-all duration-1000 ease-out"
                                    style={{ width: `${progressPercentage}%` }}
                                ></div>
                            </div>

                            {/* Date */}
                            <div className="flex justify-between items-center">
                                <span className="italic text-[10px] text-[#70AEFF]">
                                    Updated Syllabus: {new Date().toLocaleDateString('en-GB')}
                                </span>
                                <span className="text-white text-[10px]">
                                    {progressPercentage}%
                                </span>
                            </div>
                        </div>

                            </div>

                            {/* Progress Bar Section - Always visible */}

                        </div>

                        
                    </div>


                </div>
            </div>
        </div>
    )
}

export default LoaderFour
